import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { ROLE_NAV, RISK_DEPT_ROLES, PAYMENT_RAISE_ROLES } from './Navigation';
import RsrLogo from './RsrLogo';
import { C } from '../theme';

// Sidebar nav model (grouped). `roles` restricts an item for non-allow-list roles.
const GROUPS = [
  { items: [
    { label: 'Dashboard', path: '/dashboard', icon: '📊' },
    { label: 'MD Dashboard', path: '/md-dashboard', icon: '📈', roles: ['admin', 'management'] },
  ] },
  { title: 'Orders', items: [
    { label: 'Merchandising', path: '/merchandising', icon: '🛍' },
    { label: 'Fabric Calculator', path: '/fabric-calculator', icon: '🧵', roles: ['admin', 'management', 'procurement_officer', 'merchandiser'] },
    // Merchandising-owned pending-fabric queue (request-and-proceed). Count badge is the
    // non-email backstop against silent pile-up (design ruling 9).
    { label: 'Pending Fabrics', path: '/pending-fabrics', icon: '🧶', roles: ['admin', 'management', 'merchandiser'], fabricBadge: true },
    { label: 'Order Execution', path: '/order-execution', icon: '📋' },
    { label: 'Risk Analysis', path: '/risk-analysis', icon: '🛡️', roles: RISK_DEPT_ROLES },
    // Ownership-gated (NOT role-gated): shown only to a user who actually owns a
    // pending sign-off. Sign-off owners span many roles, so ownership — the live
    // count from GET /risk/sign-offs/count — is the sole visibility signal.
    { label: 'Risk Sign-offs', path: '/risk-sign-offs', icon: '✍️', ownershipBadge: true },
    { label: 'Deroi Mart Pvt Ltd', path: '/deroi', icon: '🏭', roles: ['admin', 'management', 'deroi_operator'] },
    { label: 'Procurement', path: '/procurement', icon: '🚚' },
    { label: 'Vendors', path: '/vendor-review', icon: '🏭' },
    { label: 'Job Work', path: '/job-work', icon: '✂️' },
  ] },
  { title: 'Operations', items: [
    { label: 'Production', path: '/production', icon: '⚙️' },
    { label: 'Sampling & T&M', path: '/sampling-tms', icon: '🧵', roles: ['admin', 'management', 'merchandiser', 'ppc_manager', 'production_incharge', 'production_supervisor'] },
    // Standalone screens for the Production Incharge (who has no Order Execution /
    // Merchandising access, so can't reach these via the OE / Merchandising tabs).
    { label: 'T&A', path: '/tna', icon: '📅', roles: ['production_incharge', 'production_supervisor', 'ppc_manager'] },
    { label: 'Sales Plan', path: '/sales-plan', icon: '📈', roles: ['production_incharge', 'production_supervisor', 'ppc_manager'] },
    { label: 'Full Kitting', path: '/full-kitting', icon: '📦', roles: ['production_incharge'] },
    { label: 'Store Inward', path: '/store-inward', icon: '📥', roles: ['admin', 'management', 'stores_incharge', 'store'] },
    // ADDED 2026-08-28, after both shipped with a route, a NAV_ITEMS entry and no way in.
    // Layout renders THIS file; NAV_ITEMS in Navigation.js is read by App.js for route
    // guarding and by nothing that draws a menu. Gate Register was unreachable from
    // 17 Aug and Runner from the morning of 28 Aug — the same omission twice, five weeks
    // apart, which is why scripts/check-nav-coverage.js now fails a build over it.
    { label: 'Gate Register', path: '/gate-register', icon: '🚧', roles: ['admin', 'management', 'stores_incharge', 'store', 'gate', 'deroi_operator'] },
    // NO `roles` KEY. Anyone in the office may ask for something to be fetched — that is the
    // whole point of pooling, and the request most likely to be lost is the one from somebody
    // with a narrow menu. For the twelve department-scoped roles visible() returns on the
    // ROLE_NAV allow-list before this array is read, and /runner is in all twelve already;
    // this line is what admin and management see.
    { label: 'Runner', path: '/runner', icon: '🛵' },
    { label: 'PPM', path: '/ppm', icon: '📋', roles: ['production_incharge'] },
    { label: 'Quality', path: '/quality', icon: '✅' },
    // maintenance_incharge added for correctness. NOTE it is INERT for him in practice: visible()
    // returns on the ROLE_NAV allow-list (line ~108) before an item's own roles array is read,
    // and he is now in ROLE_NAV. It matters for any role NOT in ROLE_NAV, and it must not state
    // something false either way.
    { label: 'Maintenance', path: '/maintenance', icon: '🔧', roles: ['admin', 'management', 'production_incharge', 'production_supervisor', 'ppc_manager', 'maintenance_incharge'] },
  ] },
  { title: 'Business', items: [
    // hr = Day Book verification only (mig 176). Inert for hr in practice — `visible()` returns
    // on the ROLE_NAV allow-list before this array is read for any allow-listed role — but it
    // must still say what is true, or the next reader concludes hr has no Finance access.
    { label: 'Finance', path: '/finance', icon: '💰', roles: ['admin', 'management', 'stores_incharge', 'accounts', 'hr', 'deroi_operator'] },
    { label: 'Payment Requests', path: '/requests', icon: '💳', roles: PAYMENT_RAISE_ROLES },
    { label: 'Performance', path: '/performance', icon: '📈', roles: ['admin', 'management', 'hr'] },
    { label: 'Tasks', path: '/tasks', icon: '✔️' },
  ] },
  { title: 'People', items: [
    // Huddle — the 10:05 stand-up. Everyone attends and fills in their own sheet, so no
    // roles array; the gate is the one exclusion (ruling 9) and is handled in visible().
    { label: 'Huddle', path: '/huddle', icon: '🗣️' },
    // Help Slip — no roles array and no exclusion: everyone gets it (ruling 6).
    { label: 'Help Slip', path: '/help-slip', icon: '🙋' },
    { label: 'Human Resources', path: '/hr', icon: '🧑‍💼', roles: ['admin', 'management', 'hr'] },
    { label: 'My HR', path: '/my-hr', icon: '🙋' },
    { label: 'Field Punch', path: '/field-attendance', icon: '📍' },
    { label: 'Training Assessment', path: '/assessment', icon: '🎓' },
    { label: 'Training Videos', path: '/training', icon: '🎬' },
    // No roles array on purpose — every role may open this. admin/management get the roster;
    // everyone else is served only their own activity, enforced server-side.
    { label: 'Activity Log', path: '/activity', icon: '📋' },
  ] },
  { title: 'Settings', items: [
    { label: 'Buyer Master', path: '/buyer-master', icon: '🏷', roles: ['admin', 'management'] },
    { label: 'Masters', path: '/masters', icon: '🗄' },
    { label: 'Departments', path: '/departments', icon: '🏢', roles: ['admin', 'management', 'hr'] },
    { label: 'Admin', path: '/admin/users', icon: '🛡', roles: ['admin'] },
  ] },
];

export default function Sidebar({ collapsed, onToggle }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { user } = useAuth();
  const allow = ROLE_NAV[user?.role];
  const w = collapsed ? 60 : 220;

  // Pending risk sign-off count (drives the ownership-gated Risk Sign-offs item).
  // Authenticate-only endpoint, works for every role. Do NOT swallow the error —
  // a silently-swallowed failure here is exactly what kept this screen invisible.
  const [signoffCount, setSignoffCount] = useState(0);
  useEffect(() => {
    api.get('/risk/sign-offs/count')
      .then((r) => setSignoffCount(r.data?.data?.pending || 0))
      .catch((err) => console.error('Sidebar: /risk/sign-offs/count failed', err));
  }, []);

  // Pending-fabric count for the merch nav badge. Role-gated endpoint — only the curator
  // roles get a non-403; anyone else silently shows no badge.
  const [fabricPendingCount, setFabricPendingCount] = useState(0);
  useEffect(() => {
    if (!['admin', 'management', 'merchandiser'].includes(user?.role)) return;
    api.get('/oe/fabric-master/pending/count')
      .then((r) => setFabricPendingCount(r.data?.data?.pending || 0))
      .catch((err) => console.error('Sidebar: /oe/fabric-master/pending/count failed', err));
  }, [user?.role]);

  const visible = (item) => {
    if (item.path === '/admin/users') return user?.role === 'admin';
    // My HR self-service is universal — every logged-in employee gets it,
    // regardless of any role allow-list (anyone can be named a reliever).
    if (item.path === '/my-hr') return true;
    // Training Assessment is universal too — every role takes their own test.
    if (item.path === '/assessment') return true;
    if (item.path === '/training') return true;   // Training Videos — universal (mig 291)
    // Huddle is universal EXCEPT the gate, which does not attend the stand-up (ruling 9).
    // Matches both pathAllowedForRole and the roster the server derives.
    if (item.path === '/huddle') return user?.role !== 'gate';
    if (item.path === '/help-slip') return true;   // every role, no exclusions
    // Risk Sign-offs is ownership-gated, not role-gated: visible iff the user
    // owns at least one pending sign-off.
    if (item.ownershipBadge) return signoffCount > 0;
    if (allow) return allow.includes(item.path);
    return !item.roles || item.roles.includes(user?.role);
  };
  const isActive = (path) => pathname === path || pathname.startsWith(path + '/');

  return (
    <aside style={{
      width: w, minWidth: w, background: C.sidebar, color: 'white', height: '100vh',
      position: 'sticky', top: 0, display: 'flex', flexDirection: 'column',
      transition: 'width 0.18s ease', flexShrink: 0, zIndex: 50 }}>
      {/* Logo */}
      <div onClick={() => navigate('/dashboard')} style={{ padding: collapsed ? '16px 4px' : '18px 16px', paddingBottom: 8, cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, borderBottom: '1px solid rgba(255,255,255,0.08)', overflow: 'visible' }}>
        {collapsed ? <RsrLogo width={40} showText={false} /> : <RsrLogo width={150} />}
      </div>

      {/* Nav */}
      <nav style={{ flex: 1, overflowY: 'auto', padding: '10px 0' }}>
        {GROUPS.map((g, gi) => {
          const items = g.items.filter(visible);
          if (!items.length) return null;
          return (
            <div key={gi} style={{ marginBottom: 6 }}>
              {g.title && !collapsed && (
                <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: 1.5, color: 'rgba(255,255,255,0.25)', textTransform: 'uppercase', padding: '10px 18px 4px' }}>── {g.title} ──</div>
              )}
              {g.title && collapsed && gi > 0 && <div style={{ height: 1, background: 'rgba(255,255,255,0.08)', margin: '8px 14px' }} />}
              {items.map(item => {
                const active = isActive(item.path);
                return (
                  <button key={item.path} onClick={() => navigate(item.path)} title={item.label}
                    style={{
                      width: '100%', border: 'none', cursor: 'pointer', textAlign: 'left',
                      display: 'flex', alignItems: 'center', gap: 12, whiteSpace: 'nowrap',
                      padding: collapsed ? '10px 0' : '9px 18px', justifyContent: collapsed ? 'center' : 'flex-start',
                      background: active ? C.red : 'transparent', color: active ? 'white' : 'rgba(255,255,255,0.82)',
                      fontSize: 13, fontWeight: active ? 600 : 500, borderRadius: 0,
                      transition: 'background 0.12s' }}
                    onMouseEnter={e => { if (!active) e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; }}
                    onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent'; }}>
                    <span style={{ fontSize: 16, width: 18, textAlign: 'center', flexShrink: 0 }}>{item.icon}</span>
                    {!collapsed && <span>{item.label}</span>}
                    {item.ownershipBadge && signoffCount > 0 && (
                      <span title={`${signoffCount} risk(s) awaiting your sign-off`}
                        style={{ marginLeft: collapsed ? 0 : 'auto', background: '#E22D2D', color: '#fff', borderRadius: 10, padding: '0 7px', fontSize: 11, fontWeight: 700 }}>{signoffCount}</span>
                    )}
                    {item.fabricBadge && fabricPendingCount > 0 && (
                      <span title={`${fabricPendingCount} fabric(s) pending approval`}
                        style={{ marginLeft: collapsed ? 0 : 'auto', background: '#B45309', color: '#fff', borderRadius: 10, padding: '0 7px', fontSize: 11, fontWeight: 700 }}>{fabricPendingCount}</span>
                    )}
                  </button>
                );
              })}
            </div>
          );
        })}
      </nav>

      {/* Collapse toggle */}
      <button onClick={onToggle} title={collapsed ? 'Expand' : 'Collapse'}
        style={{ border: 'none', borderTop: '1px solid rgba(255,255,255,0.08)', background: 'transparent', color: 'rgba(255,255,255,0.6)', cursor: 'pointer', padding: '12px 0', fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
        <span>{collapsed ? '»' : '«'}</span>{!collapsed && <span style={{ fontSize: 12 }}>Collapse</span>}
      </button>
    </aside>
  );
}
